import React, {useState, useEffect} from 'react'
import {useSearchParams} from 'react-router-dom'
import {GoogleLogin, CredentialResponse} from '@react-oauth/google'

import {NianticSpatialBrand} from '../components/images'
import {EmailPasswordSignIn} from '../components/EmailPasswordSignIn'

import {config} from '../config'

// The mapping of different methods of redirecting the signin page handles
const REDIRECT_TYPE = {
  UNITY_EDITOR: 'unity-editor',
  UNITY_APP: 'unity-app',
  IOS_APP: 'ios-app',
  UNITY_SAMPLE_APP: 'nsdk-unity-samples',
  NATIVE_SAMPLE_APP: 'nsdk-samples',
  ARGUS_APP: 'nsdk-argus',
  NATIVE_EXTERNAL_APP: 'nsdk-external',
  NS_FIELD_OPS: 'ns-field-ops',
}

const IOS_CUSTOM_URL_SCHEME = 'nsdk-samples://signin/redirect'
const NATIVE_SAMPLE_APP_URL_SCHEME = 'nsdk-samples://signin/redirect'
const UNITY_SAMPLE_APP_URL_SCHEME = 'nsdk-unity-samples://signin/redirect'
const ARGUS_APP_URL_SCHEME = 'nsdk-argus://signin/redirect'
const NATIVE_EXTERNAL_APP_URL_SCHEME = 'nsdk-external://signin/redirect'
const NS_FIELD_OPS_URL_SCHEME = 'ns-field-ops://signin/redirect'

const SignIn: React.FC = () => {
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSignedIn, setIsSignedIn] = useState<boolean>(false)
  const [redirectType, setRedirectType] = useState<string>('')
  const [redirectPort, setRedirectPort] = useState<string>('')
  const [useEmailPassword, setUseEmailPassword] = useState<boolean>(false)

  useEffect(() => {
    const redirectType = searchParams.get('redirectType')
    if (!redirectType) {
      setError('No redirect type provided')
      return
    }
    if (redirectType && !Object.values(REDIRECT_TYPE).includes(redirectType as any)) {
      setError('Invalid redirect type')
      return
    }

    if (redirectType && redirectType === REDIRECT_TYPE.UNITY_EDITOR) {
      const redirectPort = searchParams.get('port')
      if (!redirectPort) {
        setError('No redirect port provided')
        return
      }
      setRedirectPort(redirectPort)
    }

    setRedirectType(redirectType as keyof typeof REDIRECT_TYPE)
  }, [searchParams])


  /**
   * Handle Unity Editor redirect
  */
  const handleUnityEditorRedirect = (refreshToken: string) => {
    const redirectUrl = `http://127.0.0.1:${redirectPort}/login/success?refreshToken=${encodeURIComponent(refreshToken)}`

    if (typeof window !== 'undefined') {
      window.location.href = redirectUrl
    } else {
      console.error('Window is not defined')
      return
    }
  }


  /**
   * Handle Unity App (production flows) redirect
   */
  const handleUnityAppRedirect = (accessToken: string, refreshToken: string) => {
    const oauthRedirectUrl = `${config.nativeAppScheme}://${config.oauthCallbackPath}?accessToken=${encodeURIComponent(accessToken)}&refreshToken=${encodeURIComponent(refreshToken)}`

    if (typeof window !== 'undefined') {
      window.location.href = oauthRedirectUrl
    } else {
      console.error('Window is not defined')
      return
    }
  }

  /**
   * Handle iOS App redirect using custom url scheme
   */
  const handleIOSAppRedirect = (accessToken: string, refreshToken: string) => {
    const oauthRedirectUrl = `${IOS_CUSTOM_URL_SCHEME}?refreshToken=${encodeURIComponent(refreshToken)}&accessToken=${encodeURIComponent(accessToken)}`

    if (typeof window !== 'undefined') {
      window.location.href = oauthRedirectUrl
    } else {
      console.error('Window is not defined')
      return
    }
  }

  /**
   * Handle Native Sample App redirect using custom url scheme
   */
  const handleNativeSampleAppRedirect = (accessToken: string, refreshToken: string) => {
    const oauthRedirectUrl = `${NATIVE_SAMPLE_APP_URL_SCHEME}?refreshToken=${encodeURIComponent(refreshToken)}&accessToken=${encodeURIComponent(accessToken)}`

    if (typeof window !== 'undefined') {
      window.location.href = oauthRedirectUrl
    } else {
      console.error('Window is not defined')
      return
    }
  }

  /**
   * Handle Unity Sample App redirect using custom url scheme
   */
  const handleUnitySampleAppRedirect = (accessToken: string, refreshToken: string) => {
    const oauthRedirectUrl = `${UNITY_SAMPLE_APP_URL_SCHEME}?refreshToken=${encodeURIComponent(refreshToken)}&accessToken=${encodeURIComponent(accessToken)}`
    if (typeof window !== 'undefined') {
      window.location.href = oauthRedirectUrl
    } else {
      console.error('Window is not defined')
      return
    }
  }

  /**
   * Handle Argus App redirect using custom url scheme
   */
  const handleArgusAppRedirect = (accessToken: string, refreshToken: string) => {
    const oauthRedirectUrl = `${ARGUS_APP_URL_SCHEME}?refreshToken=${encodeURIComponent(refreshToken)}&accessToken=${encodeURIComponent(accessToken)}`

    if (typeof window !== 'undefined') {
      window.location.href = oauthRedirectUrl
    } else {
      console.error('Window is not defined')
      return
    }
  }

  /**
   * Handle Native External App redirect using custom url scheme
   */
  const handleNativeExternalAppRedirect = (accessToken: string, refreshToken: string) => {
    const oauthRedirectUrl = `${NATIVE_EXTERNAL_APP_URL_SCHEME}?refreshToken=${encodeURIComponent(refreshToken)}&accessToken=${encodeURIComponent(accessToken)}`

    if (typeof window !== 'undefined') {
      window.location.href = oauthRedirectUrl
    } else {
      console.error('Window is not defined')
      return
    }
  }

  /**
   * Handle NS Field Ops redirect using custom url scheme
   */
  const handleNSFieldOpsRedirect = (accessToken: string, refreshToken: string) => {
    const oauthRedirectUrl = `${NS_FIELD_OPS_URL_SCHEME}?refreshToken=${encodeURIComponent(refreshToken)}&accessToken=${encodeURIComponent(accessToken)}`

    if (typeof window !== 'undefined') {
      window.location.href = oauthRedirectUrl
    } else {
      console.error('Window is not defined')
      return
    }
  }

  /**
   * Shared post-auth handler: exchanges any idToken with the backend and redirects.
   */
  const handleSignIn = async (idToken: string) => {
    try {
      setIsLoading(true)
      setError('')

      const response = await fetch(config.backendUrl + '/api/signin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({idToken}),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Sign-in failed')
      }

      const data = await response.json()

      if (redirectType === REDIRECT_TYPE.UNITY_EDITOR) {
        handleUnityEditorRedirect(data.refreshToken)
      } else if (redirectType === REDIRECT_TYPE.UNITY_APP) {
        handleUnityAppRedirect(data.accessToken, data.refreshToken)
      } else if (redirectType === REDIRECT_TYPE.IOS_APP) {
        handleIOSAppRedirect(data.accessToken, data.refreshToken)
      } else if (redirectType === REDIRECT_TYPE.NATIVE_SAMPLE_APP) {
        handleNativeSampleAppRedirect(data.accessToken, data.refreshToken)
      } else if (redirectType === REDIRECT_TYPE.UNITY_SAMPLE_APP) {
        handleUnitySampleAppRedirect(data.accessToken, data.refreshToken)
      } else if (redirectType === REDIRECT_TYPE.ARGUS_APP) {
        handleArgusAppRedirect(data.accessToken, data.refreshToken)
      } else if (redirectType === REDIRECT_TYPE.NATIVE_EXTERNAL_APP) {
        handleNativeExternalAppRedirect(data.accessToken, data.refreshToken)
      } else if (redirectType === REDIRECT_TYPE.NS_FIELD_OPS) {
        handleNSFieldOpsRedirect(data.accessToken, data.refreshToken)
      }

      setIsSignedIn(true)
    } catch (err) {
      console.error('Sign-in error:', err)
      setError('Sign-in failed')
      setIsSignedIn(false)
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Handle successful Google OAuth response
   */
  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    const idToken = credentialResponse.credential
    if (!idToken) {
      setError('No credential received from Google')
      return
    }
    await handleSignIn(idToken)
  }

  /**
   * Handle Google OAuth errors
   */
  const handleGoogleError = () => {
    setError('Google Sign-In failed. Please try again.')
  }

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center space-x-3 p-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="text-primary font-medium">Signing in...</span>
        </div>
      )
    }
    if (isSignedIn) {
      return (
        <div className="text-center">
          <h2 className="text-xl font-bold text-green-600 mb-2">Successfully signed in</h2>
          <p className="text-gray-600 text-sm">You can safely close this browser window.</p>
        </div>
      )
    }
    if (error) {
      return (
        <div className="text-sm -color-error-300 text-center">
          {error}
        </div>
      )
    }
    const divider = (
      <div className='flex items-center w-full gap-3'>
        <div className='flex-1 border-t border-gray-300'></div>
        <span className='text-sm text-gray-400'>or</span>
        <div className='flex-1 border-t border-gray-300'></div>
      </div>
    )

    return (
      <div className='flex flex-col items-center gap-4 w-full'>
        {useEmailPassword ? (
          <>
            <EmailPasswordSignIn onSuccess={handleSignIn} />
            {divider}
            <button type='button' className='text-sm btn-text-link cursor-pointer' onClick={() => setUseEmailPassword(false)}>
              Sign in with Google instead
            </button>
          </>
        ) : (
          <>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap
              theme="outline"
              size="large"
              text="signin_with"
              shape="pill"
              logo_alignment="left"
            />
            {divider}
            <button type='button' className='btn btn-outline w-full' onClick={() => setUseEmailPassword(true)}>
              Login with email
            </button>
          </>
        )}
      </div>
    )
  }

  return (
    <main className='bg-default-background flex flex-col items-center justify-center h-screen'>
      <NianticSpatialBrand className='w-48 min-h-18 fill-brand mb-4'/>
      <h2 className='text-center my-6'>Welcome to Niantic Spatial Enterprise</h2>
      <div className='w-84'>
        {renderContent()}
      </div>
    </main>
  )
}

export default SignIn